import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { Spinner } from "flowbite-react";
import RequireAuth from "./AuthRouter";
import Container from "./Container";

// Páginas carregadas sob demanda (code-splitting por rota) — reduz drasticamente
// o bundle inicial, que antes carregava todas as ~60 telas de uma vez.
const LoginPage = lazy(() => import("../pages/LoginPage"));
const NotFoundPage = lazy(() => import("../pages/NotFoundPage"));
const Dashboard = lazy(() => import("../components/Dashboard"));
const MyProfilePage = lazy(() => import("../pages/MyProfilePage"));
const SupportTable = lazy(() => import("../components/tables/SupportTable"));
const DocsTable = lazy(() => import("../components/tables/DocsTable"));
const DetailsTable = lazy(() => import("../components/tables/DetailsTable"));
const UsersTable = lazy(() => import("../components/tables/UsersTable"));
const AuditsTable = lazy(() => import("../components/tables/AuditsTable"));
const AdminCustomersTable = lazy(
  () => import("../components/tables/AdminCustomersTable"),
);
const LGPDPage = lazy(() => import("../pages/LGPDPage"));
const AdminQueriesTable = lazy(
  () => import("../components/tables/AdminQueriesTable"),
);
const AdminCustomerForm = lazy(
  () => import("../components/forms/AdminCustomerForm"),
);
const InvoicesTable = lazy(() => import("../components/tables/InvoicesTable"));
const QueriesCreditTable = lazy(
  () => import("../components/tables/QueriesCreditTable"),
);
const QueriesCadastralTable = lazy(
  () => import("../components/tables/QueriesCadastralTable"),
);
const AdminRepresentativesTable = lazy(
  () => import("../components/tables/AdminRepresentativesTable"),
);
const AdminRepresentativeForm = lazy(
  () => import("../components/forms/AdminRepresentativeForm"),
);
const QueriesAutoTable = lazy(
  () => import("../components/tables/QueriesAutoTable"),
);
const PDFQueryPage = lazy(() => import("../pages/PDFQueryPage"));
const AdminHistoryTable = lazy(
  () => import("../components/tables/AdminHistoryTable"),
);
const ReportTable = lazy(() => import("../components/tables/ReportTable"));
const AdminPaymentsTable = lazy(
  () => import("../components/tables/AdminPaymentsTable"),
);
const AdminUsersTable = lazy(
  () => import("../components/tables/AdminUsersTable"),
);
const AdminTablesTable = lazy(
  () => import("../components/tables/AdminTablesTable"),
);
const ChargesTable = lazy(() => import("../components/tables/ChargesTable"));
const ReportSurveysTable = lazy(
  () => import("../components/tables/ReportSurveysTable"),
);
const Survey = lazy(() => import("../components/survey/Survey"));
const SurveysTable = lazy(() => import("../components/tables/SurveysTable"));
const SurveyConfig = lazy(() => import("../components/survey/SurveyConfig"));
const AdminReportSurveysTable = lazy(
  () => import("../components/tables/AdminReportSurveysTable"),
);
const CVsTable = lazy(() => import("../components/tables/CVsTable"));
const ComunicateSell = lazy(
  () => import("../components/comunicate-sell/ComunicateSell"),
);
const ReportCvsTable = lazy(
  () => import("../components/tables/ReportCvsTable"),
);
const AdminProvidersTable = lazy(
  () => import("../components/tables/AdminProvidersTable"),
);
const RegisterPage = lazy(() => import("../pages/RegisterPage"));
const HelpCenterTable = lazy(
  () => import("../components/tables/HelpCenterTable"),
);
const AdminHelpCenterTable = lazy(
  () => import("../components/tables/AdminHelpCenterTable"),
);
const ForgotPassword = lazy(() => import("../pages/ForgotPassword"));
const CompanyDetailsPage = lazy(() => import("../pages/CompanyDetailsPage"));
const ReportsPage = lazy(() => import("../pages/ReportsPage"));
const AdminPage = lazy(() => import("../pages/AdminPage"));
const ReviewQuery = lazy(() => import("../pages/ReviewQuery"));
const LocalizaPage = lazy(() => import("../pages/LocalizaPage"));
const ReviewDiario = lazy(() => import("../pages/ReviewDiarioPage"));
const FaqPage = lazy(() => import("../pages/FaqPage"));
const CompareQueriesPage = lazy(() => import("../pages/CompareQueriesPage"));
const CompareQueriesPicker = lazy(
  () => import("../pages/CompareQueriesPicker"),
);
const RepresentativeTablePage = lazy(
  () => import("../pages/RepresentativeTablePage"),
);
const FinancialRepresentative = lazy(
  () => import("../pages/FinancialRepresentative"),
);
const MyCustomersTable = lazy(
  () => import("../components/tables/MyCustomersTable"),
);
const NegativeListPage = lazy(
  () => import("../components/tables/NegativeListPage"),
);
const NegativeCreatePage = lazy(
  () => import("../components/negatives/NegativeCreatePage"),
);
const BackofficeNegativeTable = lazy(
  () => import("../components/tables/BackofficeNegativeTable"),
);
const CompanyDetailsSellerPage = lazy(
  () => import("../pages/CompanyDetailsSellerPage"),
);
const InvoicesPage = lazy(() => import("../pages/InvoicesPage"));
const CreditEnginePage = lazy(() => import("../pages/CreditEnginePage"));
const CreditEngineSettingsPage = lazy(
  () => import("../pages/CreditEngineSettingsPage"),
);
const BannerManagerPage = lazy(() => import("../pages/BannerManagerPage"));
const BannerCreationPage = lazy(() => import("../pages/BannerCreationPage"));
const MonitoreClientesPage = lazy(
  () => import("../pages/MonitoreClientesPage"),
);
const AdminMonitoredClientsPage = lazy(
  () => import("../pages/AdminMonitoredClientsPage"),
);

const RouteFallback = () => (
  <div
    className="flex items-center justify-center w-full"
    style={{ height: "100vh" }}
  >
    <Spinner size="xl" />
  </div>
);

const AppRouter = () => {
  return (
    <BrowserRouter>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/review-query" element={<ReviewQuery />} />
          <Route path="/recuperar-senha" element={<ForgotPassword />} />
          <Route path="/register/:uuid" element={<RegisterPage />} />
          <Route
            path="/"
            element={
              <RequireAuth>
                <Container>
                  <Outlet />
                </Container>
              </RequireAuth>
            }
          >
            <Route path="/" element={<Dashboard />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="users" element={<UsersTable />} />
            <Route path="queries/details" element={<DetailsTable />} />
            <Route path="queries/credits" element={<QueriesCreditTable />} />
            <Route
              path="/queries/compare/pick"
              element={<CompareQueriesPicker />}
            />
            <Route path="/queries/compare" element={<CompareQueriesPage />} />
            <Route
              path="queries/cadastrals"
              element={<QueriesCadastralTable />}
            />
            <Route path="queries/autos" element={<QueriesAutoTable />} />
            <Route path="query/generate-pdf" element={<PDFQueryPage />} />
            <Route path="localize" element={<LocalizaPage />} />
            <Route path="credit-engine" element={<CreditEnginePage />} />
            <Route
              path="credit-engine/settings"
              element={<CreditEngineSettingsPage />}
            />
            <Route path="negatives" element={<NegativeListPage />} />
            <Route
              path="gerenciamento-de-carteira"
              element={<MonitoreClientesPage />}
            />
            <Route path="negatives/create" element={<NegativeCreatePage />} />
            <Route path="surveys" element={<SurveysTable />} />
            <Route path="survey/new" element={<Survey />} />
            <Route path="survey/config" element={<SurveyConfig />} />
            <Route path="cvs" element={<CVsTable />} />
            <Route path="cv/new" element={<ComunicateSell />} />
            <Route path="charges" element={<ChargesTable />} />
            <Route path="reports/queries" element={<ReportTable />} />
            <Route path="reports/surveys" element={<ReportSurveysTable />} />
            <Route path="reports/cvs" element={<ReportCvsTable />} />
            <Route
              path="admin/financial-representative"
              element={<FinancialRepresentative />}
            />
            <Route path="invoices" element={<InvoicesTable />} />
            <Route path="faq" element={<FaqPage />} />
            <Route path="support" element={<HelpCenterTable />} />
            <Route path="audits" element={<AuditsTable />} />
            <Route path="admin/queries" element={<AdminQueriesTable />} />
            <Route
              path="admin/representatives"
              element={<AdminRepresentativesTable />}
            />
            <Route path="my-customers" element={<MyCustomersTable />} />
            <Route path="admin/customers" element={<AdminCustomersTable />} />
            <Route
              path="admin/customers/:uuid"
              element={<CompanyDetailsPage />}
            />
            <Route
              path="admin/customer-seller/:id"
              element={<CompanyDetailsSellerPage />}
            />
            <Route path="admin/history" element={<AdminHistoryTable />} />
            <Route
              path="admin/negatives"
              element={<BackofficeNegativeTable />}
            />
            <Route path="admin/providers" element={<AdminProvidersTable />} />
            <Route path="admin/reports" element={<ReportsPage />} />
            <Route path="admin/users" element={<AdminUsersTable />} />
            <Route path="admin/invoices" element={<InvoicesPage />} />
            <Route path="admin/daily-review" element={<ReviewDiario />} />
            <Route path="admin/new-customer" element={<AdminCustomerForm />} />
            <Route
              path="admin/new-representative"
              element={<AdminRepresentativeForm />}
            />
            <Route path="admin/payments" element={<AdminPaymentsTable />} />
            <Route path="admin/tables" element={<AdminTablesTable />} />
            <Route
              path="admin/reports/surveys"
              element={<AdminReportSurveysTable />}
            />
            <Route path="admin/support" element={<AdminHelpCenterTable />} />
            <Route
              path="admin/table-cost"
              element={<RepresentativeTablePage />}
            />
            <Route
              path="admin/banners/create"
              element={<BannerCreationPage />}
            />
            <Route
              path="admin/banners/:bannerId/edit"
              element={<BannerCreationPage />}
            />
            <Route path="admin/banners" element={<BannerManagerPage />} />
            <Route
              path="admin/monitored-clients"
              element={<AdminMonitoredClientsPage />}
            />
            <Route path="admin/admin" element={<AdminPage />} />
            <Route path="docs" element={<DocsTable />} />
            <Route path="profile" element={<MyProfilePage />} />
            <Route path="support" element={<SupportTable />} />
            <Route path="lgpd" element={<LGPDPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

export default AppRouter;
